/* eslint-disable formatjs/no-literal-string-in-jsx */
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Inline, Stack } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import type { Context } from '@atlassian/post-office-context';
import { css } from '@compiled/react';
import {
	type MessageProps,
	createPlacementComponent,
	useDisableChoreographer,
	useInitialData,
} from '@post-office/placement-common';
import { useEffect, useState } from 'react';

import { MessageRenderer, type renderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { withConfluenceHomeBannerIntlProvider } from '../__generated__/intl-provider';
import { type ConfluenceHomeBannerInitialData } from '../api/types';
import { type ConfluenceHomeBannerPlacement } from '../contracts/Placement';

export type Message = MessageProps<typeof renderer>;

type ConfluenceHomeBannerProps = {
	initialData: Promise<Response>;
	context: Context;
} & ConfluenceHomeBannerPlacement;

// https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/gates/post_office_conf_banner_shadow_traffic_in_conf
const CONFLUENCE_HOME_BANNER_SHADOW_TRAFFIC_GATE = 'post_office_conf_banner_shadow_traffic_in_conf';

const placeholderStyles = css({
	height: token('space.600', '90px'),
	padding: token('space.200', '16px'),
	overflow: 'hidden',
	border: `1px solid ${token('color.border', '#091E4224')}`,
	display: 'flex',
	flex: '1 100%',
	borderRadius: token('border.radius.050', '5px'),
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: token('space.300', '24px'),
});

const InvisibleConfluenceHomeBannerComponent = createPlacementComponent(
	PLACEMENT_ID,
	({ initialData }: ConfluenceHomeBannerProps) => {
		useInitialData(initialData);
		return null;
	},
);

export const ConfluenceHomeBannerPlacementComponent = withConfluenceHomeBannerIntlProvider(
	createPlacementComponent<ConfluenceHomeBannerPlacement>(PLACEMENT_ID, ({ initialData }) => {
		// Disabling this placement from being choreographed as it is not a pop-up
		useDisableChoreographer();

		const { data, isLoading } = useInitialData<ConfluenceHomeBannerInitialData>(initialData);
		const [messageState, setMessageState] = useState<Message[]>([]);

		useEffect(() => {
			if (!isLoading) {
				setMessageState(data?.messages as Message[]);
			}
		}, [data, isLoading]);

		if (isLoading) {
			return (
				<>
					<div css={placeholderStyles} data-testid="confluence-home-banner-loading-skeleton">
						<Inline alignInline="center" alignBlock="center" spread="space-between" grow="fill">
							<Skeleton width="145px" height="44px" borderRadius="3px" isShimmering />
							<Stack space="space.100">
								<Skeleton width="460px" height="25px" borderRadius="3px" isShimmering />
								<Skeleton width="460px" height="25px" borderRadius="3px" isShimmering />
							</Stack>
							<Skeleton width="195px" height="40px" borderRadius="3px" isShimmering />
						</Inline>
					</div>
				</>
			);
		}

		return (
			<>
				{messageState
					.filter(
						(message) =>
							message.messageTemplateId !== 'test-message-inapp' &&
							message.messageTemplateId !== 'message-template-template',
					)
					?.slice(0, 1)
					.map(({ messageInstanceId, ...props }) => (
						<MessageRenderer
							{...props}
							key={messageInstanceId}
							id={messageInstanceId}
							messageInstanceId={messageInstanceId}
							onDismissed={() => setMessageState([])}
						/>
					))}
			</>
		);
	}),
);

const ConfluenceHomeBanner = (props: ConfluenceHomeBannerProps) => {
	const isShadowTrafficEnabled = FeatureGates.checkGate(CONFLUENCE_HOME_BANNER_SHADOW_TRAFFIC_GATE);

	return isShadowTrafficEnabled ? (
		<InvisibleConfluenceHomeBannerComponent {...props} />
	) : (
		<ConfluenceHomeBannerPlacementComponent {...props} />
	);
};

export default ConfluenceHomeBanner;
